import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["detail", "menu", "nav", "navAnchor"]

  connect() {
    this.active = true
    this.links = [...this.navTarget.querySelectorAll('a[href^="#request-"]')]
    this.menuLinks = [...this.menuTarget.querySelectorAll('a[href^="#request-"]')]
    this.sections = this.links.map(link => ({
      href: link.getAttribute("href"),
      element: this.detailTarget.querySelector(link.getAttribute("href"))
    })).filter(section => section.element)
    this.resizeObserver = new ResizeObserver(() => this.resize())
    this.resizeObserver.observe(this.detailTarget)
    this.resize()
    this.navObserver = new IntersectionObserver(entries => {
      if (!this.active) return
      const entry = entries.find(item => item.target === this.navAnchorTarget)
      if (!entry) return
      const elevated = !entry.isIntersecting && entry.boundingClientRect.top < 48
      if (elevated === this.elevated) return
      this.elevated = elevated
      this.navTarget.classList.toggle("is-elevated", elevated)
    }, { rootMargin: "-48px 0px 0px 0px" })
    this.navObserver.observe(this.navAnchorTarget)
  }

  resize() {
    if (!this.active) return
    const compact = this.detailTarget.getBoundingClientRect().width < 900
    if (compact !== this.compact) {
      this.compact = compact
      this.element.dataset.requestNavigationCompact = String(compact)
      if (!compact) this.menuTarget.open = false
    }
    this.updateCurrent()
  }

  updateCurrent() {
    if (!this.active) return
    // Switch while the section's outer edge is in the gap below the bar,
    // including the native anchor's 90px scroll-margin landing position.
    const line = this.compact ? 20 : this.navTarget.getBoundingClientRect().height + 32
    const positions = this.sections.map(section => ({ ...section, top: section.element.getBoundingClientRect().top }))
    const passed = positions.filter(section => section.top <= line + 1)
    let href = null
    if (window.scrollY > 0 && passed.length) {
      const latestTop = Math.max(...passed.map(section => section.top))
      // Two columns can share a heading position. Preserve the clicked column;
      // otherwise use reading order instead of oscillating between equal tops.
      const row = passed.filter(section => Math.abs(section.top - latestTop) < 2)
      href = (row.find(section => section.href === this.preferredHref) || row[0]).href
      const bottom = window.scrollY + window.innerHeight >= this.element.ownerDocument.documentElement.scrollHeight - 2
      if (bottom) href = positions.at(-1).href
    }
    this.setCurrent(href)
  }

  setCurrent(href) {
    if (href === this.currentHref) return
    this.currentHref = href
    for (const link of [...this.links, ...this.menuLinks]) {
      if (link.getAttribute("href") === href) link.setAttribute("aria-current", "location")
      else link.removeAttribute("aria-current")
    }
  }

  select(event) {
    const link = event.target.closest("a[href^='#']")
    if (!link || event.defaultPrevented ||
        event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    this.preferredHref = link.getAttribute("href")
    this.updateCurrent()
    if (this.menuTarget.open) {
      this.menuTarget.open = false
      this.menuTarget.querySelector("summary").focus({ preventScroll: true })
    }
    // Preserve native anchors and the page's smooth-scroll/reduced-motion styles.
  }

  prepareCache() {
    // Cache preparation can leave this document visible (native fragments or an
    // interrupted visit). Stop observers only when Stimulus actually disconnects.
    this.menuTarget.open = false
  }

  reset() {
    this.active = false
    this.resizeObserver?.disconnect()
    this.navObserver?.disconnect()
    this.menuTarget.open = false
    this.navTarget.classList.toggle("is-elevated", false)
    this.setCurrent(null)
    delete this.preferredHref
    delete this.compact
    delete this.elevated
  }

  disconnect() {
    this.reset()
    delete this.element.dataset.requestNavigationCompact
  }
}
